import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { Box, Typography, useTheme } from '@mui/material';

const COLORS = ['#4caf50', '#f44336', '#ff9800', '#2196f3'];

const Charts = ({ summary }) => {
  const theme = useTheme();
  const stats = summary?.summary || {};
  const dailySummary = summary?.daily_summary || [];

  const pieData = [
    { name: 'Credit', value: stats.credit_count || 0 },
    { name: 'Debit', value: stats.debit_count || 0 },
  ];

  const barData = dailySummary.slice(0, 7).reverse().map(day => ({
    date: new Date(day.date).toLocaleDateString('en-KE', { weekday: 'short' }),
    credits: day.total_paid_in || 0,
    debits: day.total_withdrawn || 0,
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box sx={{
          bgcolor: theme.palette.background.paper,
          p: 1.5,
          borderRadius: 1,
          boxShadow: theme.shadows[3],
          border: `1px solid ${theme.palette.divider}`,
        }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Typography key={index} variant="body2" sx={{ color: entry.color }}>
              {entry.name}: KES {entry.value.toLocaleString()}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ color: theme.palette.text.primary }}>
        Transaction Overview
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, height: '100%' }}>
        <Box sx={{ flex: 2, height: 300 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Daily Trends (Last 7 Days)
          </Typography>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barData}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis dataKey="date" stroke={theme.palette.text.secondary} />
              <YAxis stroke={theme.palette.text.secondary} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="credits" fill={theme.palette.success.main} name="Credits" />
              <Bar dataKey="debits" fill={theme.palette.error.main} name="Debits" />
            </BarChart>
          </ResponsiveContainer>
        </Box>
        <Box sx={{ flex: 1, height: 300 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Transaction Distribution
          </Typography>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Box>
  );
};

export default Charts;